import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../lib/AuthContext';

export default function VerifyEmailPage() {
  const [params]              = useSearchParams();
  const navigate              = useNavigate();
  const { setAccessToken }    = useAuth();
  const token                 = params.get('token');
  const email                 = params.get('email');
  const next                  = params.get('next');
  const called                = useRef(false);

  const [status, setStatus]   = useState('loading');
  const [userData, setUserData] = useState(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (called.current) return;
    called.current = true;

    if (!token || !email) {
      setStatus('error');
      setMessage('Invalid verification link. Please try signing up again.');
      return;
    }

    axios
      .get('/api/auth/verify-email', { params: { token, email } })
      .then(res => {
        const { user, token: jwt, tokensAdded, link_id } = res.data;

        // Log the new session in
        setAccessToken(jwt);
        localStorage.setItem('hasSession', 'true');
        localStorage.setItem('userType', user.type);
        localStorage.setItem('username', user.username);

        setUserData({ user, tokensAdded, link_id });
        setStatus('success');

        // Navigate to the appropriate destination after 3s
        const dest = user.type === 'company'
          ? `/adminHUD/${encodeURIComponent(user.company_name || user.username)}`
          : next || '/dashboard';

        setTimeout(() => navigate(dest), 3000);
      })
      .catch(err => {
        setStatus('error');
        setMessage(err.response?.data?.error || 'Verification failed. The link may have expired.');
      });
  }, [token, email, navigate, setAccessToken]);

  const S = {
    page: {
      minHeight: '100vh',
      background: '#f4f4f4',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'Arial, sans-serif',
    },
    card: {
      background: '#fff',
      borderRadius: '8px',
      boxShadow: '0 2px 20px rgba(0,0,0,0.1)',
      padding: '48px 40px',
      maxWidth: '440px',
      width: '100%',
      textAlign: 'center',
    },
    icon:    { fontSize: '48px', marginBottom: '16px' },
    heading: { fontSize: '22px', fontWeight: '800', color: '#111', marginBottom: '12px' },
    body:    { fontSize: '14px', color: '#555', lineHeight: '1.6', marginBottom: '20px' },
    sub:     { fontSize: '12px', color: '#999', marginBottom: '24px' },
  };

  return (
    <div style={S.page}>
      <div style={S.card}>
        {status === 'loading' && (
          <>
            <div style={S.icon}>⏳</div>
            <div style={S.heading}>Verifying your email…</div>
            <div style={S.body}>Please wait a moment.</div>
          </>
        )}

        {status === 'success' && userData && (
          <>
            <div style={S.icon}>🎉</div>
            <div style={S.heading}>You're in, {userData.user.username}!</div>
            {userData.tokensAdded > 0 && (
              <div style={{
                background: 'linear-gradient(135deg,#eef4ff,#ddeaff)',
                border: '1.5px solid #b8d0f5', borderRadius: '12px',
                padding: '1rem 1.5rem', marginBottom: '16px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem',
              }}>
                <span style={{ fontSize: '1.8rem' }}>🪙</span>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: '1.4rem', fontWeight: 900, color: '#0d3165', lineHeight: 1 }}>{userData.tokensAdded}</div>
                  <div style={{ fontSize: '0.78rem', color: '#5580b0', fontWeight: 600 }}>Free Article Credits added to your account</div>
                </div>
              </div>
            )}
            <div style={S.body}>Your account is ready. Redirecting you now…</div>
            <div style={S.sub}>Taking you to your dashboard in a moment.</div>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={S.icon}>❌</div>
            <div style={S.heading}>Verification Failed</div>
            <div style={S.body}>{message}</div>
            <button
              onClick={() => navigate('/signup/role')}
              style={{ padding: '10px 24px', background: '#dd3333', color: '#fff', border: 'none', borderRadius: '5px', fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}
            >
              Try Signing Up Again
            </button>
          </>
        )}
      </div>
    </div>
  );
}
